import { useState, useMemo } from 'react';
import { FiSearch, FiEye, FiCalendar, FiUser, FiActivity, FiExternalLink, FiClock, FiMapPin, FiPhone, FiMonitor, FiUserCheck, FiInfo, FiCode, FiCheckCircle, FiLogOut } from 'react-icons/fi';
import api from '../services/api';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';

const SiteSearch = () => {
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cccd, setCccd] = useState('');
  const [patientName, setPatientName] = useState('');
  const [hasSearched, setHasSearched] = useState(false);
  const { user, logout } = useAuth();

  const handleSearch = async (e) => {
    e.preventDefault();

    if (!cccd || !patientName) {
      toast.warning('Vui lòng nhập đầy đủ cả Số CCCD và Họ tên');
      return;
    }

    setLoading(true);
    setHasSearched(true);

    try {
      const params = {
        page: 1,
        limit: 100,
        cccd: cccd.trim(),
        patient_name: patientName.trim(),
      };

      const response = await api.get('/site/search', { params });

      if (!response.data.success) {
        throw new Error(response.data.error?.message || 'Lỗi khi tra cứu');
      }

      const results = response.data.data || [];
      const total = response.data.pagination?.total || results.length;
      setExams(results);

      if (results.length === 0) {
        toast.info('Không tìm thấy ca chụp nào');
      } else {
        toast.success(`Tìm thấy ${total} ca chụp`);
      }
    } catch (error) {
      console.error('Search error:', error);
      toast.error('Không thể tra cứu thông tin ca chụp. Vui lòng thử lại.');
      setExams([]);
    } finally {
      setLoading(false);
    }
  };

  const getDicomViewerUrl = (studyInstanceUid) => {
    if (!studyInstanceUid) return null;

    const orthancUrl = import.meta.env.VITE_ORTHANC_URL || 'http://localhost:8042';
    const viewerUrl = import.meta.env.VITE_DICOM_VIEWER_URL || 'http://localhost:3000/viewer';

    if (viewerUrl && viewerUrl !== 'http://localhost:3000/viewer') {
      return `${viewerUrl}?studyInstanceUIDs=${studyInstanceUid}`;
    }

    return `${orthancUrl}/app/explorer.html#study?study=${studyInstanceUid}`;
  };

  const handleViewExam = async (exam) => {
    if (!exam.study_instance_uid) {
      toast.warning('Ca chụp chưa có kết quả hoặc chưa được đồng bộ');
      return;
    }

    try {
      const checkResponse = await api.post('/orthanc/check-study', {
        study_instance_uid: exam.study_instance_uid,
      });

      if (checkResponse.data.success && checkResponse.data.data.exists) {
        const viewerUrl = getDicomViewerUrl(exam.study_instance_uid);
        if (viewerUrl) {
          window.open(viewerUrl, '_blank');
        }
      } else {
        toast.warning('Ca chụp chưa có kết quả hoặc chưa được đồng bộ. Vui lòng đợi hoặc liên hệ với LocalUser.');
      }
    } catch (error) {
      console.error('Error checking study:', error);
      toast.error('Không thể kiểm tra study trong Orthanc');
    }
  };

  const handleLogout = () => {
    logout();
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      scheduled: { label: 'Đã lên lịch', class: 'bg-blue-100 text-blue-800' },
      in_progress: { label: 'Đang thực hiện', class: 'bg-yellow-100 text-yellow-800' },
      completed: { label: 'Hoàn thành', class: 'bg-green-100 text-green-800' },
      cancelled: { label: 'Đã hủy', class: 'bg-red-100 text-red-800' },
      synced: { label: 'Đã đồng bộ', class: 'bg-indigo-100 text-indigo-800' },
    };
    const config = statusConfig[status] || { label: status, class: 'bg-blue-100 text-blue-800' };
    return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.class}`}>{config.label}</span>;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('vi-VN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      });
    } catch {
      return dateString;
    }
  };

  const formatSex = (sex) => {
    if (!sex) return '-';
    const sexMap = {
      M: 'Nam',
      F: 'Nữ',
      U: 'Không xác định',
      Male: 'Nam',
      Female: 'Nữ',
      Unknown: 'Không xác định',
    };
    return sexMap[sex] || sex;
  };

  const groupedExams = useMemo(() => {
    const groups = {};
    exams.forEach((exam) => {
      const key = exam.patient_cccd || exam.cccd || exam.patient_id || 'unknown';
      if (!groups[key]) {
        groups[key] = {
          patientName: exam.patient_name || exam.patient_full_name || exam.patient_id || 'Không rõ',
          cccd: exam.patient_cccd || exam.cccd || '-',
          patientId: exam.patient_id_from_patients || exam.patient_id || '-',
          siteName: exam.site_name || '-',
          birthDate: exam.patient_birth_date || null,
          sex: exam.patient_sex || null,
          age: exam.patient_age || null,
          weight: exam.patient_weight || null,
          address: exam.patient_address || null,
          phone: exam.patient_phone || null,
          exams: [],
        };
      }
      groups[key].exams.push(exam);
    });

    Object.values(groups).forEach((group) => {
      group.exams.sort((a, b) => {
        const timeA = a.scheduled_time ? new Date(a.scheduled_time) : new Date(0);
        const timeB = b.scheduled_time ? new Date(b.scheduled_time) : new Date(0);
        return timeB - timeA;
      });
    });

    return Object.values(groups);
  }, [exams]);

  return (
    <div className="animate-fade-in">
      <div className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm mb-4">
        <div className="flex items-start gap-3 mb-4">
          <div className="p-2 rounded-full bg-primary/10 text-primary">
            <FiInfo className="text-lg" />
          </div>
          <div className="flex-1">
            <h2 className="text-sm font-semibold text-slate-900">Hướng dẫn tra cứu</h2>
            <ul className="text-xs text-slate-600 list-disc pl-4 mt-1 space-y-1">
              <li>Nhập <strong>Số CCCD</strong> và <strong>Họ tên</strong> để tra cứu ca chụp.</li>
              <li>Hệ thống sẽ trả về các ca chụp phù hợp với thông tin bệnh nhân.</li>
              <li>Có thể mở viewer nếu ca chụp đã đồng bộ ảnh.</li>
            </ul>
          </div>
        </div>

        <form onSubmit={handleSearch} className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Số CCCD</label>
            <div className="relative">
              <FiCode className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:border-primary"
                placeholder="Nhập số CCCD"
                value={cccd}
                onChange={(e) => setCccd(e.target.value)}
                disabled={loading}
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Họ tên</label>
            <div className="relative">
              <FiUser className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:border-primary"
                placeholder="Nhập họ tên bệnh nhân"
                value={patientName}
                onChange={(e) => setPatientName(e.target.value)}
                disabled={loading}
              />
            </div>
          </div>
          <div className="flex items-end">
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center justify-center gap-2 px-4 py-2 w-full md:w-auto bg-primary text-white rounded-md text-sm font-semibold hover:bg-primary-dark disabled:opacity-60"
            >
              {loading ? (
                <>
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Đang tra cứu...</span>
                </>
              ) : (
                <>
                  <FiSearch className="text-base" />
                  <span>Tra cứu</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Kết quả */}
      <div className="space-y-4">
        {hasSearched && exams.length === 0 && !loading && (
          <div className="bg-white border border-slate-200 rounded-lg p-4 text-sm text-slate-600">
            Không tìm thấy ca chụp nào khớp với thông tin đã nhập.
          </div>
        )}

        {groupedExams.map((group, idx) => (
          <div key={idx} className="bg-white border border-slate-200 rounded-lg shadow-sm">
            <div className="border-b border-slate-200 px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center text-lg font-semibold">
                  <FiUser />
                </div>
                <div>
                  <div className="text-sm font-semibold text-slate-900">{group.patientName}</div>
                  <div className="text-xs text-slate-500 flex items-center gap-2">
                    <span className="flex items-center gap-1"><FiCode className="text-slate-400" /> CCCD: {group.cccd || '-'}</span>
                    <span className="flex items-center gap-1"><FiCode className="text-slate-400" /> Mã BN: {group.patientId || '-'}</span>
                  </div>
                </div>
              </div>
              <div className="text-xs text-slate-500 flex items-center gap-2">
                <FiMapPin className="text-slate-400" /> {group.siteName}
              </div>
            </div>

            <div className="divide-y divide-slate-100">
              {group.exams.map((exam) => (
                <div key={exam.id} className="px-4 py-3 grid grid-cols-1 md:grid-cols-4 gap-3 items-start">
                  <div className="md:col-span-2">
                    <div className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                      <FiActivity className="text-primary" />
                      {exam.description || 'Ca chụp'}
                    </div>
                    <div className="text-xs text-slate-500 flex flex-wrap gap-2 mt-1">
                      <span className="flex items-center gap-1"><FiCalendar className="text-slate-400" /> {formatDate(exam.scheduled_time)}</span>
                      <span className="flex items-center gap-1"><FiClock className="text-slate-400" /> {exam.scheduled_time || '-'}</span>
                      <span className="flex items-center gap-1"><FiUserCheck className="text-slate-400" /> Bác sĩ: {exam.referring_physician_name || exam.referring_physician_id || '-'}</span>
                    </div>
                    <div className="text-xs text-slate-500 flex flex-wrap gap-2 mt-1">
                      <span className="flex items-center gap-1"><FiMapPin className="text-slate-400" /> Site: {exam.site_name || '-'}</span>
                      <span className="flex items-center gap-1"><FiMonitor className="text-slate-400" /> Máy: {exam.station_name || '-'}</span>
                      <span className="flex items-center gap-1"><FiInfo className="text-slate-400" /> Modality: {exam.modality || '-'}</span>
                    </div>
                    <div className="text-xs text-slate-500 flex flex-wrap gap-2 mt-1">
                      <span className="flex items-center gap-1"><FiPhone className="text-slate-400" /> ĐT: {group.phone || '-'}</span>
                      <span className="flex items-center gap-1"><FiActivity className="text-slate-400" /> Giới tính: {formatSex(group.sex)}</span>
                      <span className="flex items-center gap-1"><FiInfo className="text-slate-400" /> Tuổi: {group.age || '-'}</span>
                      <span className="flex items-center gap-1"><FiActivity className="text-slate-400" /> Cân nặng: {group.weight || '-'}</span>
                    </div>
                  </div>

                  <div className="md:col-span-1 flex flex-col gap-2">
                    <div className="text-xs text-slate-500">Accession: <span className="font-medium text-slate-800">{exam.accession_number || '-'}</span></div>
                    <div className="text-xs text-slate-500">External ID: <span className="font-medium text-slate-800">{exam.external_id || '-'}</span></div>
                    <div className="text-xs text-slate-500">Study UID: <span className="font-medium text-slate-800 break-all">{exam.study_instance_uid || '-'}</span></div>
                    <div>{getStatusBadge(exam.status)}</div>
                  </div>

                    <div className="md:col-span-1 flex items-center md:justify-end">
                      <button
                        onClick={() => handleViewExam(exam)}
                        className="inline-flex items-center gap-2 px-3 py-2 bg-primary text-white rounded-md text-sm font-semibold hover:bg-primary-dark"
                      >
                        <FiExternalLink className="text-base" />
                        Xem kết quả
                      </button>
                    </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SiteSearch;


